import { User, UserStatus } from '@/types'
import { Badge } from '@/components/ui/badge'
import {
  getStatusColor,
  getStatusTextColor,
  getStatusBadgeVariant,
  getStatusShortLabel,
} from '@/lib/utils/user-status-utils'
import { type TextSize, getTextSizeClasses } from '@/lib/utils/text-size-utils'

interface UserStatusDisplayProps {
  /** User object containing status information */
  user?: User
  /** Status string directly (alternative to user prop) */
  status?: UserStatus | string
  /** Size variant for the status display */
  size?: TextSize
  /** Display mode */
  variant?: 'dot-only' | 'text-only' | 'dot-text' | 'badge' | 'badge-compact'
  /** Additional CSS classes */
  className?: string
  /** Whether to show tooltip with full status text */
  showTooltip?: boolean
  /** Custom format for the status text */
  format?: 'full' | 'short' | 'custom'
  /** Custom label when format is 'custom' */
  customLabel?: string
}

/**
 * Get dot size classes based on size variant
 */
function getDotSizeClasses(size: TextSize = 'xs'): string {
  switch (size) {
    case 'xs':
      return 'h-1.5 w-1.5'
    case 'sm':
    case 'base':
      return 'h-2 w-2'
    case 'md':
      return 'h-3 w-3'
    case 'lg':
    case 'xl':
      return 'h-4 w-4'
    default:
      return 'h-2 w-2'
  }
}

/**
 * UserStatusDisplay component for consistent status rendering across the app
 */
export function UserStatusDisplay({
  user,
  status,
  size = 'xs',
  variant = 'dot-text',
  className = '',
  showTooltip = false,
  format = 'short',
  customLabel,
}: UserStatusDisplayProps) {
  // Determine the status to display
  const displayStatus = status || user?.status
  if (!displayStatus) return null

  const statusEnum = displayStatus as UserStatus
  const dotColor = getStatusColor(statusEnum)
  const textColor = getStatusTextColor(statusEnum)
  const dotSize = getDotSizeClasses(size)
  const textSize = getTextSizeClasses(size)

  // Get the label to display
  const getLabel = () => {
    if (customLabel) return customLabel
    if (format === 'full') return displayStatus
    if (format === 'short') return getStatusShortLabel(statusEnum)
    return displayStatus
  }

  const label = getLabel()

  // Dot-only variant
  if (variant === 'dot-only') {
    const dot = (
      <span
        className={`inline-block rounded-full ${dotColor} ${dotSize} ${className}`}
        title={showTooltip ? displayStatus : undefined}
      />
    )
    return dot
  }

  // Text-only variant
  if (variant === 'text-only') {
    return <span className={`${textColor} ${textSize} ${className}`}>{label}</span>
  }

  // Badge variant
  if (variant === 'badge' || variant === 'badge-compact') {
    const badgeVariant = getStatusBadgeVariant(statusEnum)
    const isCompact = variant === 'badge-compact'

    return (
      <Badge
        variant={badgeVariant}
        className={`${isCompact ? 'text-xs px-1.5 py-0.5' : ''} ${className}`}
        title={showTooltip ? displayStatus : undefined}
      >
        {isCompact && (
          <span
            className={`inline-block rounded-full ${dotColor} ${getDotSizeClasses('xs')} mr-1`}
          />
        )}
        {label}
      </Badge>
    )
  }

  // Dot-text variant (default)
  return (
    <span className={`inline-flex items-center gap-2 ${textSize} ${className}`}>
      <span
        className={`inline-block rounded-full ${dotColor} ${dotSize}`}
        title={showTooltip ? displayStatus : undefined}
      />
      <span className={textColor}>{label}</span>
    </span>
  )
}
